/* Owner Command Centre widget — Client Empowerment Fund.
 *
 * Owner/Head only (server-side enforcement via /api/fund/owner/analytics).
 * Renders spend-by-site, spend-by-category, utilisation heatmap with
 * <40% / >100% highlights, top 3 stories, top 5 people in the league,
 * and a Save-of-the-Month picker from FH nominations.
 *
 * Mounts inside CommandCentrePage; hidden for non-Owner/Head via the
 * 403 that /owner/analytics returns.
 */
import { useEffect, useState } from "react";
import { Sparkles, TrendingUp, TrendingDown, Trophy, Heart, Award } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const CATEGORY_COLOURS = {
    recovery:         { bg: "bg-amber-50",   dot: "bg-amber-500",   text: "text-amber-800",   label: "Service recovery" },
    relationship:     { bg: "bg-emerald-50", dot: "bg-emerald-500", text: "text-emerald-800", label: "Relationship" },
    moment_of_truth:  { bg: "bg-violet-50",  dot: "bg-violet-500",  text: "text-violet-800",  label: "Moment-of-truth" },
};

const fmt = (n) => n == null ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function FundWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/fund/owner/analytics");
            setData(data);
        } catch (e) {
            // 403 = not Owner/Head — hide silently
            if (e?.response?.status !== 403) toast.error(apiError(e));
            setData(false);
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function nominate(entryId, on) {
        try {
            await api.patch(`/fund/entries/${entryId}/nominate?nominate=${on ? "true" : "false"}`);
            await load();
        } catch (e) { toast.error(apiError(e)); }
    }
    async function pickSaveOfMonth(entry) {
        const citation = prompt(`Citation for Save of the Month — ${entry.logged_by_name}\n\n"${entry.one_line_story}"`);
        if (!citation) return;
        try {
            await api.post("/fund/awards", { kind: "save_of_month", user_id: entry.logged_by_id, entry_id: entry.id, citation });
            toast.success("Save of the Month awarded (+5 stars, ₹2,000 payroll note)");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    }

    if (loading) return <div className="mt-8 h-40 rounded-xl bg-white animate-pulse" data-testid="fund-widget-loading" />;
    if (data === false) return null;  // hidden for non-Owner/Head
    if (!data) return null;

    const totalSpend = data?.totals?.spend || 0;
    const walletCount = data?.totals?.wallets || 0;
    const entryCount = data?.totals?.entries || 0;
    const attention = (data.utilisation || []).filter((u) => u.attention);

    return (
        <section className="mt-8" data-testid="fund-widget">
            <h2 className="font-display font-bold text-sm uppercase tracking-widest text-[#1E3355] mb-2 inline-flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" /> Client Empowerment Fund — {data.month}
                <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/fund/owner/monthly-pdf?month=${data.month}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="fund-widget-pdf-link"
                    className="ml-3 inline-flex items-center gap-1 rounded-full bg-[#1E3355] hover:bg-[#152541] text-white normal-case tracking-normal text-[11px] font-semibold px-3 py-1"
                >
                    Monthly PDF ↗
                </a>
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Totals + Category breakdown */}
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="fund-widget-totals">
                    <div className="text-xs uppercase tracking-widest text-gray-500">Spend this month</div>
                    <div className="mt-1 text-3xl font-bold text-[#1E3355]">₹{fmt(totalSpend)}</div>
                    <div className="text-[11px] text-gray-500">{entryCount} entries · {walletCount} wallets</div>

                    <div className="mt-4 space-y-1.5">
                        {(data.by_category || []).map((c) => {
                            const cfg = CATEGORY_COLOURS[c.category] || { dot: "bg-gray-400", label: c.category };
                            const pct = totalSpend ? Math.round((c.spend / totalSpend) * 100) : 0;
                            return (
                                <div key={c.category} className="flex items-center gap-2 text-xs">
                                    <span className={`h-2.5 w-2.5 rounded-full ${cfg.dot}`} />
                                    <span className="w-32 text-gray-700">{cfg.label}</span>
                                    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                                        <div className={`h-full ${cfg.dot}`} style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="w-20 text-right font-semibold text-[#1E3355]">₹{fmt(c.spend)}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Utilisation heatmap */}
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="fund-widget-utilisation">
                    <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Wallet utilisation</div>
                    <div className="space-y-2 text-xs max-h-56 overflow-auto pr-1">
                        {(data.utilisation || []).map((u) => {
                            const cold = u.utilisation_pct < 40;
                            const hot = u.utilisation_pct > 100;
                            const pillCls = hot ? "bg-red-100 text-red-800" : cold ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800";
                            const Icon = hot ? TrendingUp : cold ? TrendingDown : Sparkles;
                            return (
                                <div key={u.wallet_id} className="flex items-center gap-2" data-testid={`fund-util-${u.wallet_id}`}>
                                    <div className="w-24 truncate text-[#1E3355] font-medium">{u.site_name}</div>
                                    <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${pillCls}`}>
                                        <Icon className="w-3 h-3" /> {u.utilisation_pct}%
                                    </span>
                                    <div className="w-20 text-right text-gray-500">₹{fmt(u.spent)} / ₹{fmt(u.allowance)}</div>
                                </div>
                            );
                        })}
                    </div>
                    {attention.length > 0 && (
                        <div className="mt-2 text-[11px] text-gray-500">
                            <span className="text-amber-700 font-semibold">{attention.length}</span> wallet(s) need attention
                        </div>
                    )}
                </div>

                {/* Best stories */}
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="fund-widget-stories">
                    <div className="text-xs uppercase tracking-widest text-gray-500 mb-2 inline-flex items-center gap-1.5">
                        <Heart className="w-3.5 h-3.5 text-red-500" /> 3 best stories
                    </div>
                    <div className="space-y-2 text-xs">
                        {(data.best_stories || []).slice(0, 3).map((s) => (
                            <div key={s.id} className="rounded-lg bg-gradient-to-br from-amber-50 to-white border border-amber-100 p-2.5">
                                <div className="font-medium text-[#1E3355] mb-0.5">{s.client_or_tenant}</div>
                                <div className="italic text-gray-700 leading-snug">"{s.one_line_story}"</div>
                                <div className="mt-1 text-[10px] text-gray-500">— {s.logged_by_name} · ₹{fmt(s.amount)}</div>
                            </div>
                        ))}
                        {(!data.best_stories || data.best_stories.length === 0) && (
                            <div className="text-gray-400 italic">No verified stories yet this month.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* League + Save of the Month */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="fund-widget-league">
                    <div className="text-xs uppercase tracking-widest text-gray-500 mb-2 inline-flex items-center gap-1.5">
                        <Trophy className="w-3.5 h-3.5 text-amber-500" /> League — top 5
                    </div>
                    <ol className="space-y-1.5 text-sm">
                        {(data.top_people || []).map((p, i) => (
                            <li key={p.user_id} className="flex items-center gap-2" data-testid={`fund-league-row-${i}`}>
                                <span className="w-6 text-right text-gray-400 font-semibold">{i + 1}.</span>
                                <span className="flex-1 text-[#1E3355] font-medium truncate">{p.user_name}</span>
                                {p.badge && (
                                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        p.badge === "gold_client_hero" ? "bg-amber-100 text-amber-800" :
                                        p.badge === "silver_client_hero" ? "bg-gray-200 text-gray-800" :
                                        "bg-orange-100 text-orange-800"
                                    }`}>
                                        <Award className="w-3 h-3" /> {p.badge.replace("_client_hero","").toUpperCase()}
                                    </span>
                                )}
                                <span className="ml-2 text-amber-600 font-bold">★ {p.points}</span>
                            </li>
                        ))}
                        {(!data.top_people || data.top_people.length === 0) && <li className="text-gray-400 italic">No stars yet this month.</li>}
                    </ol>
                </div>

                <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="fund-widget-nominations">
                    <div className="text-xs uppercase tracking-widest text-gray-500 mb-2 inline-flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-emerald-500" /> Save of the Month · nominations
                    </div>
                    <div className="space-y-2 text-xs">
                        {(data.save_of_month_nominations || []).length === 0 && (
                            <div className="text-gray-400 italic">
                                FHs haven't nominated any entry yet — encourage them to hit the <span className="rounded bg-gray-100 px-1">Nominate</span> button on verified saves.
                            </div>
                        )}
                        {(data.save_of_month_nominations || []).map((e) => (
                            <div key={e.id} className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-2.5" data-testid={`fund-nom-${e.id}`}>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1">
                                        <div className="font-medium text-[#1E3355]">{e.client_or_tenant} <span className="text-gray-400 font-normal">· {e.logged_by_name}</span></div>
                                        <div className="italic text-gray-700 mt-0.5">"{e.one_line_story}"</div>
                                        <div className="mt-0.5 text-[10px] text-gray-500">₹{fmt(e.amount)}</div>
                                    </div>
                                    <button
                                        onClick={() => pickSaveOfMonth(e)}
                                        data-testid={`fund-pick-save-${e.id}`}
                                        className="shrink-0 rounded-full bg-[#E02B2B] hover:bg-[#c72525] text-white text-xs font-semibold px-3 py-1.5 shadow-sm"
                                    >
                                        Pick 🏆
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
